import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './BillingDashboard.css';

interface Student {
  id: string;
  first_name?: string;
  firstName?: string; // API might use camelCase
  last_name?: string;
  lastName?: string; // API might use camelCase
  parent_id?: string;
  parentId?: string; // API might use camelCase
  parent_name?: string;
  parentName?: string; // API might use camelCase
  parent?: {
    id: string;
    first_name?: string;
    firstName?: string;
    last_name?: string;
    lastName?: string;
    email: string;
  };
}

interface Organization {
  id: string;
  name: string;
  organization_type: string;
  contact_name?: string;
  contact_email?: string;
  billing_email?: string;
}

interface InvoiceItem {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  item_type: string;
  competition_id?: string;
  competition_enrollment_id?: string;
}

interface CompetitionEnrollment {
  id: string;
  competition_id: string;
  competition_name?: string;
  competition_type?: string;
  team_name?: string;
  team_size?: number;
  enrollment_date: string;
  session_pack_cost?: number;
  tshirt_cost?: number;
  team_registration_share?: number;
  tournament_registration_share?: number;
  other_costs_share?: number;
  total_cost: number;
  status: string;
}

interface CreateInvoiceProps {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  paymentPlanId?: string;
  studentId?: string;
}

const CreateInvoice: React.FC<CreateInvoiceProps> = ({
  show,
  onClose,
  onSuccess,
  paymentPlanId,
  studentId
}) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [invoiceType, setInvoiceType] = useState<'parent' | 'organization'>('parent');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [competitionEnrollments, setCompetitionEnrollments] = useState<CompetitionEnrollment[]>([]);
  const [selectedCompetitionEnrollmentId, setSelectedCompetitionEnrollmentId] = useState<string>('');
  const [formData, setFormData] = useState({
    invoice_type: 'parent' as 'parent' | 'organization',
    parent_id: '',
    student_id: studentId || '',
    organization_id: '',
    payment_plan_id: paymentPlanId || '',
    plan_type: 'monthly',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    billing_period_start: '',
    billing_period_end: '',
    subtotal: 0,
    tax: 0,
    total: 0,
    notes: '',
    terms: '',
    payment_instructions: ''
  });
  const [items, setItems] = useState<InvoiceItem[]>([
    { description: '', quantity: 1, unit_price: 0, line_total: 0, item_type: 'base_fee' }
  ]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        invoice_type: 'parent' as 'parent' | 'organization',
        parent_id: '',
        student_id: studentId || '',
        organization_id: '',
        payment_plan_id: paymentPlanId || '',
        plan_type: 'monthly',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        billing_period_start: '',
        billing_period_end: '',
        subtotal: 0,
        tax: 0,
        total: 0,
        notes: '',
        terms: '',
        payment_instructions: ''
      });
      setItems([{ description: '', quantity: 1, unit_price: 0, line_total: 0, item_type: 'base_fee' }]);
      setError(null);
      setStudentSearchTerm('');
      setShowStudentDropdown(false);
      setCompetitionEnrollments([]);
      setSelectedCompetitionEnrollmentId('');
    }
  }, [show, studentId, paymentPlanId]);

  useEffect(() => {
    if (show) {
      fetchStudents();
      fetchOrganizations();
      if (paymentPlanId) {
        fetchPaymentPlanDetails();
      }
      if (studentId) {
        setFormData(prev => ({ ...prev, student_id: studentId }));
        // Set search term when student is pre-selected
        const preSelectedStudent = students.find(s => s.id === studentId);
        if (preSelectedStudent) {
          const studentName = `${preSelectedStudent.first_name || preSelectedStudent.firstName || ''} ${preSelectedStudent.last_name || preSelectedStudent.lastName || ''}`.trim();
          setStudentSearchTerm(studentName);
        }
      }
    }
  }, [show, paymentPlanId, studentId]);

  const fetchOrganizations = async () => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${config.BACKEND_URL}/api/organizations/?status=active`, { headers });
      setOrganizations(response.data || []);
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      // Don't show error, just log it
    }
  };

  const fetchStudents = async () => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      
      if (!token) {
        setError('No authentication token available. Please log in again.');
        return;
      }
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      console.log('🔍 Fetching students from:', `${config.BACKEND_URL}/api/students`);
      console.log('🔍 Authorization header present:', !!headers.Authorization);
      console.log('🔍 Token length:', token.length);
      
      const response = await axios.get(`${config.BACKEND_URL}/api/students`, { 
        headers,
        validateStatus: (status) => status < 500 // Don't throw on 4xx errors, let us handle them
      });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response headers:', response.headers);
      
      if (response.status === 401) {
        console.error('❌ 401 Unauthorized - token might be expired or invalid');
        console.error('❌ Response data:', response.data);
        setError('Authentication failed. Please log in again.');
        return;
      }
      
      if (response.status >= 400) {
        console.error('❌ Error response:', response.status, response.data);
        setError(response.data?.detail || response.data?.message || 'Failed to load students');
        return;
      }
      
      // The API returns { students: [...], total: number }
      const studentsData = response.data?.students || response.data || [];
      console.log('✅ Students loaded:', studentsData.length);
      console.log('✅ Students data:', studentsData);
      console.log('✅ Response data structure:', response.data);
      setStudents(studentsData);
      setError(null);
    } catch (err: any) {
      console.error('❌ Error fetching students:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      console.error('❌ Error config:', err.config);
      
      // Check if it's a network error or redirect issue
      if (err.code === 'ERR_FR_TOO_MANY_REDIRECTS' || err.response?.status === 307 || err.response?.status === 301) {
        console.log('🔄 Redirect detected, trying direct URL...');
        // Try again with the exact URL
        try {
          const { getValidToken } = await import('../../utils/tokenRefresh');
          const token = await getValidToken();
          const headers = { Authorization: `Bearer ${token}` };
          const response = await axios.get(`${config.BACKEND_URL}/api/students`, { headers });
          const studentsData = response.data?.students || response.data || [];
          setStudents(studentsData);
          setError(null);
          return;
        } catch (retryErr: any) {
          console.error('❌ Retry failed:', retryErr);
        }
      }
      
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to load students';
      setError(errorMessage);
    }
  };

  const fetchPaymentPlanDetails = async () => {
    if (!paymentPlanId) return;
    
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/payment-plans/${paymentPlanId}`,
        { headers }
      );
      const plan = response.data;
      
      setFormData(prev => ({
        ...prev,
        student_id: plan.student_id,
        parent_id: plan.parent_id,
        plan_type: plan.plan_type,
        billing_period_start: plan.start_date || '',
        billing_period_end: plan.end_date || ''
      }));
    } catch (err: any) {
      console.error('Error fetching payment plan:', err);
      setError('Failed to load payment plan details');
    }
  };

  // Filter students based on search term
  const filteredStudents = React.useMemo(() => {
    if (!studentSearchTerm) {
      return students;
    }
    const searchLower = studentSearchTerm.toLowerCase();
    return students.filter(student => {
      const firstName = (student.first_name || student.firstName || '').toLowerCase();
      const lastName = (student.last_name || student.lastName || '').toLowerCase();
      const fullName = `${firstName} ${lastName}`;
      const parentEmail = (student.parent?.email || '').toLowerCase();
      const parentName = (student.parent_name || student.parentName || 
        (student.parent ? `${student.parent.first_name || student.parent.firstName || ''} ${student.parent.last_name || student.parent.lastName || ''}` : '')).toLowerCase();
      
      return fullName.includes(searchLower) || 
             firstName.includes(searchLower) || 
             lastName.includes(searchLower) ||
             parentEmail.includes(searchLower) ||
             parentName.includes(searchLower);
    });
  }, [students, studentSearchTerm]);

  const fetchCompetitionEnrollments = async (studentId: string) => {
    if (!studentId || formData.plan_type !== 'competition') {
      setCompetitionEnrollments([]);
      return;
    }

    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${config.BACKEND_URL}/api/competitions/student/${studentId}`,
        { headers }
      );

      // Filter for FLL Challenge or FLL Explore competitions
      const enrollments = (response.data || []).filter((enrollment: CompetitionEnrollment) => {
        return enrollment.competition_type === 'fll_challenge' || enrollment.competition_type === 'fll_explore';
      });

      setCompetitionEnrollments(enrollments);
    } catch (err: any) {
      console.error('Error fetching competition enrollments:', err);
      setCompetitionEnrollments([]);
    }
  };

  const handleCompetitionEnrollmentChange = async (enrollmentId: string) => {
    setSelectedCompetitionEnrollmentId(enrollmentId);
    
    if (!enrollmentId || !formData.student_id) {
      setItems([{ description: '', quantity: 1, unit_price: 0, line_total: 0, item_type: 'base_fee' }]);
      return;
    }

    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Call backend API to calculate FLL invoice items
      const response = await axios.post(
        `${config.BACKEND_URL}/api/billing/invoices/calculate-fll-items`,
        {
          student_id: formData.student_id,
          competition_enrollment_id: enrollmentId
        },
        { headers }
      );

      const calculatedItems = response.data.items || [];
      
      if (calculatedItems.length > 0) {
        setItems(calculatedItems.map((item: any) => ({
          description: item.description,
          quantity: item.quantity || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          line_total: parseFloat(item.line_total) || 0,
          item_type: item.item_type || 'base_fee',
          competition_id: item.competition_id,
          competition_enrollment_id: item.competition_enrollment_id
        })));
        
        // Calculate totals
        const subtotal = calculatedItems.reduce((sum: number, item: any) => sum + parseFloat(item.line_total || 0), 0);
        const tax = subtotal * 0.13; // 13% HST
        const total = subtotal + tax;
        
        setFormData(prev => ({
          ...prev,
          subtotal: parseFloat(subtotal.toFixed(2)),
          tax: parseFloat(tax.toFixed(2)),
          total: parseFloat(total.toFixed(2))
        }));
      }
    } catch (err: any) {
      console.error('Error calculating FLL invoice items:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to calculate FLL invoice items');
    }
  };

  const handleStudentChange = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (student && formData.invoice_type === 'parent') {
      setFormData(prev => ({
        ...prev,
        student_id: studentId,
        parent_id: student.parent_id || student.parentId || ''
      }));
      const studentName = `${student.first_name || student.firstName || ''} ${student.last_name || student.lastName || ''}`.trim();
      setStudentSearchTerm(studentName);
    } else if (student && formData.invoice_type === 'organization') {
      // For organization invoices, just set student_id, don't set parent_id
      setFormData(prev => ({
        ...prev,
        student_id: studentId
      }));
      const studentName = `${student.first_name || student.firstName || ''} ${student.last_name || student.lastName || ''}`.trim();
      setStudentSearchTerm(studentName);
    } else {
      setFormData(prev => ({
        ...prev,
        student_id: studentId || '',
        parent_id: studentId ? '' : ''
      }));
      if (!studentId) {
        setStudentSearchTerm('');
      }
    }

    // Fetch competition enrollments if plan_type is competition
    if (formData.plan_type === 'competition' && studentId) {
      fetchCompetitionEnrollments(studentId);
    } else {
      setCompetitionEnrollments([]);
      setSelectedCompetitionEnrollmentId('');
    }
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate line_total
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].line_total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setItems(newItems);
    calculateTotals(newItems);
  };

  const calculateTotals = (itemsList: InvoiceItem[]) => {
    const subtotal = itemsList.reduce((sum, item) => sum + item.line_total, 0);
    const tax = subtotal * 0.13; // 13% HST (adjust as needed)
    const total = subtotal + tax;
    
    setFormData(prev => ({
      ...prev,
      subtotal: parseFloat(subtotal.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      total: parseFloat(total.toFixed(2))
    }));
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, line_total: 0, item_type: 'base_fee' }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      calculateTotals(newItems);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate form based on invoice type
      if (formData.invoice_type === 'parent') {
        if (!formData.student_id || !formData.parent_id) {
          throw new Error('Please select a student for parent invoice');
        }
      } else if (formData.invoice_type === 'organization') {
        if (!formData.organization_id) {
          throw new Error('Please select an organization');
        }
      }
      
      // Validate competition enrollment if plan_type is competition
      if (formData.plan_type === 'competition') {
        if (!selectedCompetitionEnrollmentId) {
          throw new Error('Please select a competition enrollment');
        }
        if (!formData.student_id) {
          throw new Error('Please select a student for competition invoice');
        }
      }
      
      if (items.some(item => !item.description || item.unit_price <= 0)) {
        throw new Error('Please fill in all invoice items with valid amounts');
      }
      if (formData.total <= 0) {
        throw new Error('Invoice total must be greater than 0');
      }

      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const invoiceData = {
        invoice_type: formData.invoice_type,
        parent_id: formData.invoice_type === 'parent' ? formData.parent_id : null,
        student_id: formData.student_id || null,
        organization_id: formData.invoice_type === 'organization' ? formData.organization_id : null,
        payment_plan_id: formData.payment_plan_id || null,
        plan_type: formData.plan_type,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        billing_period_start: formData.billing_period_start || null,
        billing_period_end: formData.billing_period_end || null,
        subtotal: formData.subtotal,
        tax: formData.tax,
        total: formData.total,
        amount_paid: 0,
        notes: formData.notes || null,
        terms: formData.terms || null,
        payment_instructions: formData.payment_instructions || null,
        items: items.map((item, index) => ({
          line_number: index + 1,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_total: item.line_total,
          item_type: item.item_type,
          competition_id: item.competition_id || null,
          competition_enrollment_id: item.competition_enrollment_id || null
        }))
      };

      let response;
      if (paymentPlanId) {
        // Create invoice from payment plan
        response = await axios.post(
          `${config.BACKEND_URL}/api/billing/invoices/payment-plan/${paymentPlanId}`,
          invoiceData,
          { headers }
        );
      } else {
        // Create manual invoice
        response = await axios.post(
          `${config.BACKEND_URL}/api/billing/invoices`,
          invoiceData,
          { headers }
        );
      }

      alert('Invoice created successfully!');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  console.log('🔍 CreateInvoice render - show:', show, 'paymentPlanId:', paymentPlanId);

  if (!show) {
    console.log('🔍 CreateInvoice returning null because show is false');
    return null;
  }

  const selectedStudent = students.find(s => s.id === formData.student_id);

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{ 
        zIndex: 10000,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div 
        className="modal-content billing-modal" 
        onClick={(e) => e.stopPropagation()}
        style={{
          zIndex: 10001,
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '1000px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        <div className="modal-header">
          <h2>{paymentPlanId ? 'Create Invoice from Payment Plan' : 'Create New Invoice'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '30px' }}>
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="error-message" style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                {error}
              </div>
            )}

          {/* Invoice Type Selection */}
          <div className="form-row">
            <div className="form-group">
              <label>Invoice Type *</label>
              <select
                value={formData.invoice_type}
                onChange={(e) => {
                  const newType = e.target.value as 'parent' | 'organization';
                  setFormData({ 
                    ...formData, 
                    invoice_type: newType,
                    parent_id: newType === 'organization' ? '' : formData.parent_id,
                    student_id: newType === 'organization' ? '' : formData.student_id,
                    organization_id: newType === 'parent' ? '' : formData.organization_id
                  });
                  setInvoiceType(newType);
                }}
                disabled={!!paymentPlanId}
                required
              >
                <option value="parent">Parent Invoice</option>
                <option value="organization">Organization Invoice (Clubs, Scouts, etc.)</option>
              </select>
            </div>
          </div>

          {/* Parent Invoice Fields */}
          {formData.invoice_type === 'parent' && (
            <div className="form-row">
              <div className="form-group">
                <label>Student *</label>
                <div className="searchable-select-container">
                  <input
                    type="text"
                    className="searchable-select-input"
                    placeholder="Type to search students..."
                    value={(() => {
                      if (studentSearchTerm) return studentSearchTerm;
                      if (formData.student_id) {
                        const selectedStudent = students.find(s => s.id === formData.student_id);
                        if (selectedStudent) {
                          return `${selectedStudent.first_name || selectedStudent.firstName || ''} ${selectedStudent.last_name || selectedStudent.lastName || ''}`.trim();
                        }
                      }
                      return '';
                    })()}
                    onChange={(e) => {
                      setStudentSearchTerm(e.target.value);
                      setShowStudentDropdown(true);
                      if (!e.target.value) {
                        handleStudentChange('');
                      }
                    }}
                    onFocus={() => setShowStudentDropdown(true)}
                    disabled={!!studentId || !!paymentPlanId}
                    required
                  />
                  {showStudentDropdown && !studentId && !paymentPlanId && (
                    <>
                      <div 
                        className="searchable-select-overlay"
                        onClick={() => setShowStudentDropdown(false)}
                      />
                      <div className="searchable-select-dropdown">
                        {filteredStudents.length === 0 ? (
                          <div className="searchable-select-option no-results">
                            {studentSearchTerm ? 'No students found' : 'No students available'}
                          </div>
                        ) : (
                          filteredStudents.map(student => {
                            const studentName = `${student.first_name || student.firstName || ''} ${student.last_name || student.lastName || ''}`.trim();
                            const parentEmail = student.parent?.email || '';
                            
                            return (
                              <div
                                key={student.id}
                                className={`searchable-select-option ${formData.student_id === student.id ? 'selected' : ''}`}
                                onClick={() => {
                                  handleStudentChange(student.id);
                                  setShowStudentDropdown(false);
                                }}
                              >
                                <strong>{studentName}</strong>
                                {parentEmail && <span className="parent-email"> ({parentEmail})</span>}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Organization Invoice Fields */}
          {formData.invoice_type === 'organization' && (
            <div className="form-row">
              <div className="form-group">
                <label>Organization *</label>
                <select
                  value={formData.organization_id}
                  onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
                  required
                >
                  <option value="">Select an organization</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.organization_type})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Student (Optional)</label>
                <select
                  value={formData.student_id || ''}
                  onChange={(e) => setFormData({ ...formData, student_id: e.target.value || '' })}
                >
                  <option value="">No specific student (private session)</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.first_name} {student.last_name}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Leave blank for general organization invoices, or select a student for specific private sessions
                </small>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Plan Type *</label>
              <select
                value={formData.plan_type}
                onChange={(e) => {
                  setFormData({ ...formData, plan_type: e.target.value });
                  // Fetch competition enrollments if switching to competition type
                  if (e.target.value === 'competition' && formData.student_id) {
                    fetchCompetitionEnrollments(formData.student_id);
                  } else {
                    setCompetitionEnrollments([]);
                    setSelectedCompetitionEnrollmentId('');
                  }
                }}
                required
              >
                <option value="monthly">Monthly Plan</option>
                <option value="session_package">Session Package</option>
                <option value="camp">Camp</option>
                <option value="competition">Competition</option>
                {formData.invoice_type === 'organization' && (
                  <option value="private_session">Private Session</option>
                )}
              </select>
            </div>
          </div>

          {formData.plan_type === 'competition' && formData.student_id && (
            <div className="form-row">
              <div className="form-group">
                <label>Competition Enrollment *</label>
                {competitionEnrollments.length === 0 ? (
                  <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '6px', color: '#666' }}>
                    No FLL Challenge or FLL Explore enrollments found for this student.
                  </div>
                ) : (
                  <select
                    value={selectedCompetitionEnrollmentId}
                    onChange={(e) => handleCompetitionEnrollmentChange(e.target.value)}
                    required
                  >
                    <option value="">Select a competition enrollment...</option>
                    {competitionEnrollments.map((enrollment) => (
                      <option key={enrollment.id} value={enrollment.id}>
                        {enrollment.competition_name || 'Competition'} 
                        {enrollment.team_name ? ` - Team: ${enrollment.team_name}` : ''}
                        {enrollment.team_size ? ` (Team Size: ${enrollment.team_size})` : ''}
                        {enrollment.total_cost ? ` - Total: $${enrollment.total_cost.toFixed(2)}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Select the FLL competition enrollment to invoice. Costs will be calculated automatically based on team size.
                </small>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Invoice Date *</label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Due Date *</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Billing Period Start</label>
              <input
                type="date"
                value={formData.billing_period_start}
                onChange={(e) => setFormData({ ...formData, billing_period_start: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Billing Period End</label>
              <input
                type="date"
                value={formData.billing_period_end}
                onChange={(e) => setFormData({ ...formData, billing_period_end: e.target.value })}
              />
            </div>
          </div>

          <div className="invoice-items-section">
            <div className="section-header">
              <h3>Invoice Items *</h3>
              <button type="button" className="btn btn-secondary" onClick={addItem}>
                + Add Item
              </button>
            </div>

            {items.map((item, index) => (
              <div key={index} className="invoice-item-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Description</label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Item description"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={item.item_type}
                    onChange={(e) => handleItemChange(index, 'item_type', e.target.value)}
                  >
                    <option value="base_fee">Base Fee</option>
                    <option value="registration_fee">Registration Fee</option>
                    <option value="team_cost">Team Cost</option>
                    <option value="session_fee">Session Fee</option>
                    <option value="discount">Discount</option>
                    <option value="tax">Tax</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Unit Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Line Total</label>
                  <input
                    type="text"
                    value={`$${item.line_total.toFixed(2)}`}
                    readOnly
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>

                {items.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeItem(index)}
                    style={{ marginTop: '25px', padding: '5px 10px' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="invoice-totals">
            <div className="total-row">
              <span>Subtotal:</span>
              <span>${formData.subtotal.toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span>Tax (13% HST):</span>
              <span>${formData.tax.toFixed(2)}</span>
            </div>
            <div className="total-row total-final">
              <span><strong>Total:</strong></span>
              <span><strong>${formData.total.toFixed(2)}</strong></span>
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Additional notes for the invoice"
            />
          </div>

          <div className="form-group">
            <label>Payment Instructions</label>
            <textarea
              value={formData.payment_instructions}
              onChange={(e) => setFormData({ ...formData, payment_instructions: e.target.value })}
              rows={2}
              placeholder="Payment instructions for the parent"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoice;

